import { motion } from 'framer-motion';
import { Check, X, Sparkles, Shield, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { goToApp } from '@/lib/track';

const plans = [
  {
    name: 'Plano Básico',
    price: 'R$ 0',
    period: 'para começar',
    description: '7 dias com acesso total. Depois, só o essencial para registrar.',
    features: [
      { text: '7 dias de acesso premium completo', included: true },
      { text: 'Checklist diário', included: true },
      { text: 'Monitoramento de ciclo', included: true },
      { text: 'Diário de saúde', included: true },
      { text: 'Rastreador de água', included: true },
      { text: 'Chat com IA (após o trial)', included: false },
      { text: 'Relatórios e padrões (após o trial)', included: false },
      { text: 'Conteúdo completo (após o trial)', included: false },
    ],
    cta: 'Começar grátis agora',
    highlight: false,
    icon: null,
  },
  {
    name: 'Mensal',
    price: 'R$ 19,90',
    period: '/mês',
    description: 'Flexibilidade total para você',
    subtitle: 'apenas R$0,66 por dia',
    features: [
      { text: 'Tudo do plano básico', included: true },
      { text: 'Chat com IA ilimitado', included: true },
      { text: 'Relatórios e padrões avançados', included: true },
      { text: 'Todo conteúdo educativo', included: true },
      { text: 'Programas guiados', included: true },
      { text: 'Insights personalizados', included: true },
      { text: 'Histórico completo', included: true },
      { text: 'Suporte prioritário', included: true },
    ],
    cta: 'Assinar Mensal',
    highlight: false,
    icon: Zap,
  },
  {
    name: 'Anual',
    price: 'R$ 149',
    period: '/ano',
    description: 'R$0,41/dia · Economia de R$89,80',
    originalPrice: 'R$ 238,80',
    features: [
      { text: 'Tudo do plano mensal', included: true },
      { text: 'Chat com IA ilimitado', included: true },
      { text: 'Relatórios e padrões avançados', included: true },
      { text: 'Todo conteúdo educativo', included: true },
      { text: 'Programas guiados', included: true },
      { text: 'Insights personalizados', included: true },
      { text: 'Histórico completo', included: true },
      { text: 'Suporte prioritário', included: true },
    ],
    cta: 'Assinar Anual',
    highlight: true,
    icon: Sparkles,
    badge: 'Recomendado',
  },
  {
    name: 'Vitalício',
    price: 'R$ 397',
    period: 'único',
    description: 'Acesso para sempre',
    subtitle: 'Sobe para R$597 após o lançamento',
    features: [
      { text: 'Tudo do plano anual', included: true },
      { text: 'Acesso vitalício', included: true },
      { text: 'Todas atualizações futuras', included: true },
      { text: 'Chat com IA ilimitado', included: true },
      { text: 'Relatórios e padrões avançados', included: true },
      { text: 'Todo conteúdo educativo', included: true },
      { text: 'Programas guiados', included: true },
      { text: 'Suporte VIP', included: true },
    ],
    cta: 'Comprar Vitalício',
    highlight: false,
    icon: Shield,
    badge: 'Oferta de Fundadora',
  },
];

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function PricingSection() {

  return (
    <section
      id="planos"
      data-testid="pricing-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FAADC7] to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-sm font-semibold text-[#B54182] uppercase tracking-widest mb-4 block">
            Planos e Preços
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Continue no{' '}
            <span className="text-[#B54182]">controle</span>
            {' '}— comece de graça
          </h2>
          <p className="text-base md:text-lg text-stone-500">
            7 dias com acesso completo, sem cartão de crédito. Depois decida com calma.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {plans.map((plan) => {
            const Icon = plan.icon;
            return (
              <motion.div
                key={plan.name}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                className={`relative rounded-3xl p-6 md:p-8 transition-all duration-300 ${
                  plan.highlight
                    ? 'text-white shadow-2xl shadow-[#B54182]/30 scale-[1.02] lg:scale-105 z-10'
                    : 'bg-white border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)]'
                }`}
                style={plan.highlight ? { background: 'linear-gradient(180deg, #B54182, #BE5791)' } : undefined}
                data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {plan.badge && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FAADC7] hover:bg-[#FAADC7]/90 text-[#B54182] px-4 py-1 text-xs font-bold uppercase tracking-wider shadow-lg border-0">
                    {plan.badge}
                  </Badge>
                )}

                <div className="mb-6">
                  {Icon && (
                    <div className={`w-10 h-10 rounded-xl ${plan.highlight ? 'bg-white/20' : 'bg-[#FAADC7]/20'} flex items-center justify-center mb-4`}>
                      <Icon size={20} className={plan.highlight ? 'text-white' : 'text-[#B54182]'} />
                    </div>
                  )}
                  <h3
                    className={`text-lg font-bold mb-1 ${plan.highlight ? 'text-white' : 'text-stone-900'}`}
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {plan.name}
                  </h3>
                  <p className={`text-sm ${plan.highlight ? 'text-[#FAADC7]' : 'text-stone-400'}`}>
                    {plan.description}
                  </p>
                </div>

                <div className="mb-6">
                  {plan.originalPrice && (
                    <span className={`text-sm line-through ${plan.highlight ? 'text-[#FAADC7]/70' : 'text-stone-400'}`}>
                      {plan.originalPrice}
                    </span>
                  )}
                  <div className="flex items-baseline gap-1">
                    <span className={`text-3xl md:text-4xl font-bold ${plan.highlight ? 'text-white' : 'text-stone-900'}`} style={{ fontFamily: 'var(--font-heading)' }}>
                      {plan.price}
                    </span>
                    <span className={`text-sm ${plan.highlight ? 'text-[#FAADC7]' : 'text-stone-400'}`}>
                      {plan.period}
                    </span>
                  </div>
                  {plan.subtitle && (
                    <p className={`text-xs mt-1 font-medium ${plan.highlight ? 'text-[#FAADC7]' : 'text-[#B54182]'}`}>
                      {plan.subtitle}
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      {f.included ? (
                        <Check size={16} className={`mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-[#FAADC7]' : 'text-emerald-500'}`} />
                      ) : (
                        <X size={16} className="mt-0.5 flex-shrink-0 text-stone-300" />
                      )}
                      <span className={f.included ? (plan.highlight ? 'text-white/90' : 'text-stone-600') : 'text-stone-300'}>
                        {f.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => goToApp(plan.name)}
                  className={`w-full rounded-full py-3.5 font-semibold text-sm transition-all duration-300 ${
                    plan.highlight
                      ? 'bg-white text-[#B54182] hover:bg-[#FAADC7]/30 shadow-lg'
                      : plan.name === 'Plano Básico'
                        ? 'text-white shadow-lg hover:shadow-[#B54182]/25'
                        : 'bg-[#FAADC7]/20 text-[#B54182] hover:bg-[#FAADC7]/30'
                  }`}
                  style={plan.name === 'Plano Básico' && !plan.highlight ? { background: 'linear-gradient(135deg, #B54182, #BE5791)' } : undefined}
                  data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {plan.cta}
                </motion.button>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Guarantee */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-3 bg-emerald-50 text-emerald-700 rounded-full px-6 py-3 text-sm font-medium">
            <Shield size={18} />
            7 dias de garantia de reembolso &middot; Cancele a qualquer momento &middot; Sem taxa &middot; Sem cartão de crédito para começar
          </div>
        </motion.div>
      </div>
    </section>
  );
}
